/** What one answer cost in model work (list 79.3): trips, tokens, and whether
 *  the turn hit a cutoff. Counts only — a money figure would be an estimate
 *  against a price list this page does not know. */

import { useState } from "react";

interface TripUsage {
  input_tokens: number;
  output_tokens: number;
  cache_read_input_tokens: number;
  cache_creation_input_tokens: number;
  tool_calls: number;
  /** "tools" (the model asked for tool calls), "answer" (it answered), or
   *  "synthesis" (the answer forced from what was gathered after a cutoff). */
  kind: string;
}

interface Tokenomics {
  trips: TripUsage[];
  turn_token_budget: number | null;
  max_roundtrips: number;
}

function parseTokenomics(json: string | null): Tokenomics | null {
  if (!json) return null;
  try {
    const parsed = JSON.parse(json) as Partial<Tokenomics>;
    if (!Array.isArray(parsed.trips)) return null;
    return {
      trips: parsed.trips,
      turn_token_budget: parsed.turn_token_budget ?? null,
      max_roundtrips: parsed.max_roundtrips ?? 0,
    };
  } catch {
    return null;
  }
}

function tripTotal(t: TripUsage): number {
  return (
    t.input_tokens +
    t.output_tokens +
    t.cache_read_input_tokens +
    t.cache_creation_input_tokens
  );
}

const KIND_LABEL: Record<string, string> = {
  tools: "Tool calls",
  answer: "Answer",
  synthesis: "Answer after the cutoff",
};

function cutoff(
  stopReason: string | null,
  ledger: Tokenomics | null,
): { hit: boolean; text: string } {
  switch (stopReason) {
    case "stop":
      return { hit: false, text: "No. The model finished on its own." };
    case "max_tokens":
      return {
        hit: true,
        text: "Yes: the model's output limit cut the answer off.",
      };
    case "max_roundtrips":
      return {
        hit: true,
        text: ledger?.max_roundtrips
          ? `Yes: the round-trip cap of ${ledger.max_roundtrips} model calls.`
          : "Yes: the round-trip cap.",
      };
    case "token_budget":
      return {
        hit: true,
        text: ledger?.turn_token_budget
          ? `Yes: the turn's token budget of ${ledger.turn_token_budget.toLocaleString()}.`
          : "Yes: the turn's token budget.",
      };
    case "no_progress":
      return {
        hit: true,
        text: "Yes: stopped for making no progress (the same call repeating, or only errors).",
      };
    case null:
    case "":
      return { hit: false, text: "Not recorded." };
    default:
      return { hit: false, text: `Stop reason: ${stopReason}.` };
  }
}

export interface TokenomicsSource {
  stop_reason: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
  llm_calls: number | null;
  cache_read_tokens: number | null;
  cache_write_tokens: number | null;
  tokenomics_json: string | null;
}

export function TokenomicsPanel({ row }: { row: TokenomicsSource }) {
  const ledger = parseTokenomics(row.tokenomics_json);
  const stop = cutoff(row.stop_reason, ledger);
  const input = row.input_tokens ?? 0;
  const output = row.output_tokens ?? 0;
  const cacheRead = row.cache_read_tokens ?? 0;
  const cacheWrite = row.cache_write_tokens ?? 0;
  const total = input + output + cacheRead + cacheWrite;
  const ledgerTotal = ledger
    ? Math.max(
        1,
        ledger.trips.reduce((n, t) => n + tripTotal(t), 0),
      )
    : 1;

  const figures: [string, string][] = [
    [
      "Model calls",
      row.llm_calls != null ? row.llm_calls.toLocaleString() : "not recorded",
    ],
    [
      "Input tokens",
      row.input_tokens != null ? input.toLocaleString() : "not recorded",
    ],
    [
      "Output tokens",
      row.output_tokens != null ? output.toLocaleString() : "not recorded",
    ],
    [
      "Cache read",
      row.cache_read_tokens != null
        ? cacheRead.toLocaleString()
        : "not recorded",
    ],
    [
      "Cache write",
      row.cache_write_tokens != null
        ? cacheWrite.toLocaleString()
        : "not recorded",
    ],
    ["Total tokens", total.toLocaleString()],
  ];

  return (
    <section
      aria-label="Tokenomics"
      className="mt-2 rounded border border-border bg-background p-3 text-xs"
    >
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-3 lg:grid-cols-6">
        {figures.map(([label, value]) => (
          <div key={label}>
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="font-mono text-sm text-foreground">{value}</dd>
          </div>
        ))}
      </dl>

      <div
        className={
          stop.hit
            ? "mt-3 rounded border border-tres-warning/40 bg-tres-warning/10 px-2 py-1 text-tres-warning"
            : "mt-3 text-muted-foreground"
        }
      >
        <span className="font-medium">Hit a cutoff? </span>
        {stop.text}
      </div>

      {ledger ? (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[36rem] border-collapse text-left">
            <thead className="text-muted-foreground">
              <tr>
                <th className="py-1 pr-2 font-medium">Trip</th>
                <th className="py-1 pr-2 font-medium">Kind</th>
                <th className="py-1 pr-2 text-right font-medium">Tool calls</th>
                <th className="py-1 pr-2 text-right font-medium">Input</th>
                <th className="py-1 pr-2 text-right font-medium">Output</th>
                <th className="py-1 pr-2 text-right font-medium">Cache read</th>
                <th className="py-1 pr-2 text-right font-medium">
                  Cache write
                </th>
                <th className="w-1/4 py-1 font-medium">Share of the turn</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {ledger.trips.map((t, i) => (
                <tr key={i} className="border-t border-border">
                  <td className="py-1 pr-2">{i + 1}</td>
                  <td className="py-1 pr-2 font-sans">
                    {KIND_LABEL[t.kind] ?? t.kind}
                  </td>
                  <td className="py-1 pr-2 text-right">{t.tool_calls}</td>
                  <td className="py-1 pr-2 text-right">
                    {t.input_tokens.toLocaleString()}
                  </td>
                  <td className="py-1 pr-2 text-right">
                    {t.output_tokens.toLocaleString()}
                  </td>
                  <td className="py-1 pr-2 text-right">
                    {t.cache_read_input_tokens.toLocaleString()}
                  </td>
                  <td className="py-1 pr-2 text-right">
                    {t.cache_creation_input_tokens.toLocaleString()}
                  </td>
                  <td className="py-1">
                    <div
                      role="presentation"
                      className="h-2 rounded bg-tres-action/60"
                      style={{
                        width: `${(tripTotal(t) / ledgerTotal) * 100}%`,
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {cacheRead === 0 && cacheWrite === 0 && (
            <p className="mt-2 text-muted-foreground">
              No prompt-cache tokens were reported for this answer. Of the
              providers TReS calls, only Claude reports them separately.
            </p>
          )}
        </div>
      ) : (
        <p className="mt-3 text-muted-foreground">
          Per-trip counts were not recorded for this answer. Answers recorded
          before they were kept, and turns that ended in an error, carry only
          their input and output totals.
        </p>
      )}
    </section>
  );
}

/** What the chat response carries of its own cost — the same figures a history
 *  row stores, before they are stored. */
export interface AnswerCost {
  stop_reason: string;
  usage?: {
    input_tokens: number;
    output_tokens: number;
    cache_read_input_tokens?: number;
    cache_creation_input_tokens?: number;
  };
  llm_calls: number;
  trips: TripUsage[];
  turn_token_budget?: number;
  max_roundtrips: number;
}

/** The Tokenomics link on the answer just given, so the cost is one press from
 *  the answer it bought and not only in the history list. */
export function AnswerTokenomics({ response }: { response: AnswerCost }) {
  const [open, setOpen] = useState(false);
  const { usage } = response;
  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="text-xs text-muted-foreground underline hover:text-foreground"
      >
        Tokenomics
      </button>
      {open && (
        <TokenomicsPanel
          row={{
            stop_reason: response.stop_reason,
            input_tokens: usage?.input_tokens ?? null,
            output_tokens: usage?.output_tokens ?? null,
            llm_calls: response.llm_calls,
            cache_read_tokens: usage?.cache_read_input_tokens ?? null,
            cache_write_tokens: usage?.cache_creation_input_tokens ?? null,
            tokenomics_json: JSON.stringify({
              trips: response.trips,
              turn_token_budget: response.turn_token_budget ?? null,
              max_roundtrips: response.max_roundtrips,
            }),
          }}
        />
      )}
    </div>
  );
}
